// src/pages/ArticleDetail.js
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getTitle, getImage, getDescriptionHtml, formatDate, localePath, getExcerpt,
} from "../utils/content";
import { fetchArticleById, fetchRelatedArticles } from "../utils/fetchers";
import Seo, { absoluteUrl } from "../components/Seo";

/** Full-width hero fallback when an article has no image */
function HeroImageFallback({ title = "" }) {
  const initials = title
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

  return (
    <div className="detail-hero detail-hero--fallback" aria-hidden="true">
      <svg
        viewBox="0 0 900 380"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid slice"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id="heroFallbackGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--ink, #0d1b2a)" />
            <stop offset="60%" stopColor="#112233" />
            <stop offset="100%" stopColor="#0a1520" />
          </linearGradient>
          <radialGradient id="heroGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--gold, #c9a84c)" stopOpacity="0.08" />
            <stop offset="100%" stopColor="var(--gold, #c9a84c)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width="900" height="380" fill="url(#heroFallbackGrad)" />
        <rect width="900" height="380" fill="url(#heroGlow)" />

        {/* Large decorative scales */}
        <g transform="translate(320, 40)" stroke="var(--gold, #c9a84c)" strokeWidth="1.2" fill="none" opacity="0.12">
          <line x1="130" y1="0" x2="130" y2="260" />
          <line x1="50" y1="290" x2="210" y2="290" />
          <line x1="70" y1="60" x2="190" y2="60" />
          <path d="M70 60 l-45 90 a45 45 0 0 0 90 0 l-45-90z" />
          <path d="M190 60 l-45 90 a45 45 0 0 0 90 0 l-45-90z" />
        </g>

        {/* Horizontal rule lines */}
        <line x1="60" y1="340" x2="840" y2="340" stroke="var(--gold, #c9a84c)" strokeWidth="0.5" opacity="0.2" />
        <line x1="60" y1="40" x2="840" y2="40" stroke="var(--gold, #c9a84c)" strokeWidth="0.5" opacity="0.2" />

        {/* Corner brackets */}
        <g stroke="var(--gold, #c9a84c)" strokeWidth="2" opacity="0.5">
          <polyline points="60,60 60,40 80,40" fill="none" />
          <polyline points="820,40 840,40 840,60" fill="none" />
          <polyline points="60,320 60,340 80,340" fill="none" />
          <polyline points="820,340 840,340 840,320" fill="none" />
        </g>

        {/* Center initials */}
        {initials && (
          <text
            x="450"
            y="200"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--gold, #c9a84c)"
            fontSize="88"
            fontFamily="Georgia, 'Times New Roman', serif"
            letterSpacing="14"
            opacity="0.22"
          >
            {initials}
          </text>
        )}

        {/* CCMA label */}
        <text
          x="450"
          y="260"
          textAnchor="middle"
          fill="var(--gold, #c9a84c)"
          fontSize="11"
          fontFamily="Georgia, serif"
          letterSpacing="6"
          opacity="0.35"
        >
          CCMA · DAJC
        </text>
      </svg>
    </div>
  );
}

export default function ArticleDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroError, setHeroError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setArticle(null);
    setHeroError(false);

    Promise.all([fetchArticleById(id), fetchRelatedArticles(id, 4)])
      .then(([a, r]) => {
        if (cancelled) return;
        setArticle(a);
        setRelated(r);
        setLoading(false);
      })
      .catch(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <section style={{ minHeight: "60vh" }}>
        <div className="loading"><div className="spinner" /></div>
      </section>
    );
  }
  if (!article) {
    return (
      <section style={{ minHeight: "60vh" }}>
        <div className="container empty">
          <h3>{t("common.not_found_title")}</h3>
          <p>{t("common.not_found_text")}</p>
          <Link to={localePath(lang)} className="btn btn-ghost" style={{ marginTop: 24 }}>
            {t("common.back_home")}
          </Link>
        </div>
      </section>
    );
  }

  const title = getTitle(article, lang, 200);
  const heroImg = getImage(article, lang);
  const html = getDescriptionHtml(article, lang);
  const dateStr = formatDate(article.starts_at || article.created_at, lang, t);

  // Show fallback if: no URL at all, or URL failed to load
  const showFallback = !heroImg || heroError;

  return (
    <>
      <Seo
        path={`article/${article.id}`}
        title={title}
        description={getExcerpt(article, lang, 30)}
        image={heroImg && !heroError ? heroImg : undefined}
        type="article"
        publishedTime={article.starts_at || article.created_at}
        breadcrumb={[
          { name: t("common.home"), path: "" },
          { name: t("menu.publications"), path: "publications" },
          { name: title, path: `article/${article.id}` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: title,
          description: getExcerpt(article, lang, 30),
          inLanguage: lang,
          datePublished: article.starts_at || article.created_at,
          dateModified: article.updated_at || article.starts_at || article.created_at,
          image: heroImg && !heroError ? heroImg : undefined,
          mainEntityOfPage: absoluteUrl(`article/${article.id}`, lang),
          publisher: { "@type": "Organization", name: t("brand.full") },
        }}
      />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <Link to={localePath(lang, "publications")}>{t("menu.publications")}</Link>
              <span>/</span>
              <span>{title.slice(0, 60)}{title.length > 60 ? "…" : ""}</span>
            </div>
            <h1>{title}</h1>
            <p style={{ color: "var(--gold-soft)", fontSize: 13, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 18 }}>
              {dateStr}
            </p>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="detail-layout">
            <article className="detail-content">

              {/* Hero image with fallback */}
              {showFallback ? (
                <HeroImageFallback title={title} />
              ) : (
                <div className="detail-hero">
                  <img
                    src={heroImg}
                    alt={title}
                    onError={() => setHeroError(true)}
                  />
                </div>
              )}

              <div className="detail-meta">
                <span className="date">{dateStr}</span>
                <span style={{ color: "var(--line-strong)" }}>·</span>
                <span>{t("brand.short")}</span>
              </div>

              {html ? (
                <div dangerouslySetInnerHTML={{ __html: html }} />
              ) : null}

              <div style={{ marginTop: 56, paddingTop: 32, borderTop: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                <Link to={localePath(lang, "publications")} className="btn btn-ghost">
                  ← {t("articles.view_all")}
                </Link>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className="dajc-icon-btn"
                    onClick={() => navigator.share?.({ url: window.location.href, title })}
                    aria-label={t("common.share")}
                  >
                    <i className="bi bi-share" />
                  </button>
                  <a
                    className="dajc-icon-btn"
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank" rel="noopener noreferrer"
                  >
                    <i className="bi bi-twitter-x" />
                  </a>
                  <a
                    className="dajc-icon-btn"
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                    target="_blank" rel="noopener noreferrer"
                  >
                    <i className="bi bi-linkedin" />
                  </a>
                </div>
              </div>
            </article>

            <aside className="detail-aside">
              <div className="aside-card">
                <h4>{t("articles.related")}</h4>
                <ul>
                  {related.length === 0 && <li style={{ color: "var(--muted)", fontSize: 14 }}>—</li>}
                  {related.map((r) => (
                    <li key={r.id}>
                      <Link to={localePath(lang, `article/${r.id}/${r.slug || ""}`)}>
                        {getTitle(r, lang, 70)}
                      </Link>
                      <div style={{ fontSize: 11, color: "var(--gold-deep)", marginTop: 4, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                        {formatDate(r.starts_at || r.created_at, lang, t)}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="aside-card">
                <h4>{t("footer.ccma_title")}</h4>
                <ul>
                  <li><Link to={localePath(lang, "page/12")}>{t("ccma.pages.presentation")}</Link></li>
                  <li><Link to={localePath(lang, "page/2")}>{t("ccma.pages.arbitration_request")}</Link></li>
                  <li><Link to={localePath(lang, "page/18")}>{t("ccma.pages.arbitration_rules")}</Link></li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}